import { Link } from 'react-router-dom';

import { RevenueTrend } from '../components/Charts.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { Badge, Card, Empty, Notice, Spinner, StatTile } from '../components/Ui.tsx';
import { api } from '../lib/api.ts';
import { useAsync, useAuth } from '../lib/auth.tsx';
import { money, moneyShort, number, percent, seriesColor } from '../lib/format.ts';

export function OverviewPage() {
  const { me } = useAuth();
  const summary = useAsync(() => api.insights.summary(), []);
  const performance = useAsync(() => api.insights.performance(30, 'day'), []);
  const mispricing = useAsync(() => api.insights.mispricing(), []);
  const marketStats = useAsync(() => api.market.stats(), []);

  if (summary.loading) return <Spinner />;
  const data = summary.data;

  if (!data || (data.connectionHealth === 'none' && data.listedItems === 0)) {
    return (
      <>
        <header className="page-head">
          <div>
            <h1>Overview</h1>
            <p>Find your shops and your insights fill in immediately.</p>
          </div>
        </header>
        <Empty title="No shops connected yet">
          <p className="muted">
            Enter your in-game name on the Connections page. Your prices, stock and competitors come
            from public market data - no API key needed.
          </p>
          <Link to="/connections">
            <button className="primary">Find my shops</button>
          </Link>
        </Empty>
      </>
    );
  }

  const growth =
    data.revenuePrev30d > 0
      ? ((data.revenue30d - data.revenuePrev30d) / data.revenuePrev30d) * 100
      : null;

  // Total revenue across every shop, summed into one line - one series, one
  // axis, no legend.
  const combined = new Map<string, number>();
  for (const shop of performance.data?.shops ?? []) {
    for (const point of shop.series) {
      combined.set(point.bucket, (combined.get(point.bucket) ?? 0) + point.revenue);
    }
  }
  const trend = [...combined.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([bucket, revenue]) => ({ bucket, revenue }));

  const topFindings = (mispricing.data?.rows ?? [])
    .filter((row) => row.verdict !== 'competitive')
    .slice(0, 5);

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Your shops</h1>
          <p>
            {data.shopNames.length > 0 ? data.shopNames.join(', ') : 'Your connected account'} -{' '}
            {number(data.listedItems)} items listed, benchmarked against the live ChestShop market
            {marketStats.data?.stats
              ? ` (${number(marketStats.data.stats.activeShops)} shops server-wide)`
              : ''}
            .
          </p>
        </div>
        {!data.hasSalesData && <Badge tone="muted">public data only</Badge>}
      </header>

      {!data.hasSalesData && (
        <div style={{ marginBottom: 16 }}>
          <Notice>
            <strong>Everything here is your own shop, not the whole server.</strong> Prices, stock
            and competitor comparisons come from public market data and are live now. Revenue and
            sales timing need an account webhook, which is why those read as unavailable rather than
            zero. <Link to="/connections">Connect one</Link> to fill them in.
          </Notice>
        </div>
      )}

      <div className="grid tiles">
        <StatTile
          label="Items you list"
          value={number(data.listedItems)}
          foot={<span className="muted">across your shops</span>}
        />
        <StatTile
          label="Out of stock now"
          value={number(data.outOfStockNow)}
          foot={
            data.outOfStockNow > 0 ? (
              <Link to="/restock">Which ones</Link>
            ) : (
              <span className="muted">every shelf has stock</span>
            )
          }
        />
        <StatTile
          label="Mispriced vs market"
          value={number(data.mispricedItems)}
          foot={
            data.mispricedItems > 0 ? (
              <Link to="/pricing">Review prices</Link>
            ) : (
              <span className="muted">all inside the market band</span>
            )
          }
        />
        <StatTile
          label="Revenue, 30 days"
          value={data.hasSalesData ? moneyShort(data.revenue30d) : 'n/a'}
          foot={
            !data.hasSalesData ? (
              <span className="muted">needs a webhook</span>
            ) : growth == null ? (
              <span className="muted">no prior period to compare</span>
            ) : (
              <>
                <span className={growth >= 0 ? 'delta-up' : 'delta-down'}>{percent(growth)}</span>
                <span className="muted">vs previous 30 days</span>
              </>
            )
          }
        />
        {/* Withheld rather than charged on top, so this is money that never
            reached the account - the revenue tile above is the gross. */}
        <StatTile
          label="Sales tax withheld, 30 days"
          value={data.hasSalesData ? moneyShort(data.taxWithheld30d) : 'n/a'}
          foot={
            !data.hasSalesData ? (
              <span className="muted">needs a webhook</span>
            ) : (
              <span className="muted">
                {/* Not percent(), which signs its output: "+2.5%" reads as a
                    change rather than a rate. */}
                {(data.taxRate * 100).toFixed(1)}% of sales - {moneyShort(data.netRevenue30d)} kept
              </span>
            )
          }
        />
      </div>

      <Card
        title="Your biggest pricing gaps"
        subtitle="Your price against what shops with stock actually charge, ranked by money at risk"
        actions={<Link to="/pricing">All items</Link>}
      >
        {topFindings.length === 0 ? (
          <Empty title="Nothing is badly mispriced">
            <span className="muted">Every item you list sits inside its market band.</span>
          </Empty>
        ) : (
          <DataTable
            rows={topFindings}
            rowKey={(row) => row.itemCode}
            initialSort={{ key: 'delta', dir: 'desc' }}
            columns={[
              {
                key: 'item',
                header: 'Item',
                sortValue: (row) => row.displayName,
                render: (row) => row.displayName,
              },
              {
                key: 'yours',
                header: 'Your price',
                align: 'num',
                sortValue: (row) => row.yourPrice,
                render: (row) => money(row.yourPrice),
              },
              {
                key: 'market',
                header: 'Market',
                align: 'num',
                sortValue: (row) => row.benchmark,
                render: (row) => money(row.benchmark),
              },
              {
                key: 'delta',
                header: 'Gap',
                align: 'num',
                sortValue: (row) => Math.abs(row.deltaPct),
                render: (row) => percent(row.deltaPct),
              },
              {
                key: 'verdict',
                header: 'Verdict',
                sortValue: (row) => row.verdict,
                render: (row) => (
                  <Badge tone={row.verdict === 'overpriced' ? 'critical' : 'warning'}>
                    {row.verdict}
                  </Badge>
                ),
              },
              {
                key: 'shops',
                header: 'Shops',
                align: 'num',
                sortValue: (row) => row.sampleSize,
                render: (row) => number(row.sampleSize),
              },
            ]}
          />
        )}
      </Card>

      <Card
        title="Revenue trend"
        subtitle="Daily realized revenue across your shops - from your account webhook"
        actions={<Link to="/performance">Per-shop breakdown</Link>}
      >
        {trend.length === 0 ? (
          <Empty title="No sales data yet">
            <span className="muted">
              Revenue and timing come from your account webhook. Everything else on this page works
              without it.
            </span>
          </Empty>
        ) : (
          <RevenueTrend
            data={trend}
            series={[{ key: 'revenue', label: 'Revenue', color: seriesColor(0) }]}
          />
        )}
      </Card>

      {me?.subscriber.tier === 'free' && (
        <div style={{ marginTop: 16 }}>
          <Notice>
            <strong>You are on the free teaser.</strong> It shows the first few items only. Standard
            unlocks every item you list, undercut alerts, the demand heatmap and restock detection.{' '}
            <Link to="/billing">See plans</Link>
          </Notice>
        </div>
      )}
    </>
  );
}
